import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { createAppKit } from '@reown/appkit/react';
import { EthersAdapter } from '@reown/appkit-adapter-ethers';
import { defineChain } from '@reown/appkit/networks';
import { useAppKitAccount, useAppKitNetwork, useAppKit, useDisconnect, useAppKitProvider } from '@reown/appkit/react';
import KroToCandySwapABI from '../abi/KroToCandySwap.json';
import { ethers } from 'ethers';

const KRO_TO_CANDY_SWAP_ADDRESS = '0x4C77a572EEeCa9D986eB5358fD46fe1CB093D3DD';

// Define the chain
// const ethereumMainnet = /*#__PURE__*/ defineChain({
//   id: 1,
//   name: 'Ethereum Mainnet',
//   nativeCurrency: {
//     decimals: 18,
//     name: 'Ether',
//     symbol: 'ETH',
//   },
//   rpcUrls: {
//     default: { http: ['https://rpc.ankr.com/eth'] },
//     public: { http: ['https://rpc.ankr.com/eth'] },
//   },
//   blockExplorers: {
//     default: {
//       name: 'Etherscan',
//       url: 'https://etherscan.io',
//     },
//   },
//   testnet: false,
// })

const zkcandyMainnet = /*#__PURE__*/ defineChain({
  id: 320,
  name: 'ZKcandy Mainnet',
  nativeCurrency: {
    decimals: 18,
    name: 'Mainnet ETH',
    symbol: 'ETH',
  },
  rpcUrls: {
    default: { http: ['https://rpc.zkcandy.io'] },
  },
  blockExplorers: {
    default: {
      name: 'zkCandy Mainnet Explorer',
      url: 'https://explorer.zkcandy.io/',
    },
  },
  testnet: false,
})

const metadata = {
  name: '$KRO Swap Page',
  description: 'The official $KRO Swap Site for ZKcandy x Kroma merger. Choose your reward tier based on your activity, lock in your allocation, and join the next chapter of AI-powered onchain entertainment with ZKcandy.',
  url: window.location.origin,
  icons: [`${window.location.origin}/favicon.ico`],
};

// Initialize AppKit
createAppKit({
  adapters: [new EthersAdapter()],
  projectId: '5061a4b8a67aa40618bfa9dfcdda07a9',
  networks: [zkcandyMainnet],
  defaultNetwork: zkcandyMainnet,
  metadata,
  themeVariables: {
    "--w3m-accent": "#9333EA",
    "--w3m-border-radius-master": "1px",
    "--w3m-font-family": "Montserrat, sans-serif",
  },
  features: {
    email: false,
    socials: false,
  }
});

const oracleAbi = [
  "function getUserStatus(address user) external view returns (uint8)"
];

const Web3Context = createContext();

export const useWeb3 = () => useContext(Web3Context);

export const Web3Provider = ({ children }) => {

  // Use Reown hooks
  const { isConnected, address } = useAppKitAccount();
  const { chainId } = useAppKitNetwork();
  const { open } = useAppKit();
  const { disconnect } = useDisconnect();
  const { walletProvider } = useAppKitProvider('eip155');
  const [kroSwapContract, setKroSwapContract] = useState(null);

  const [userStatus, setUserStatus] = useState(null);
  const [baseRate, setBaseRate] = useState(null);
  const [engagedRate, setEngagedRate] = useState(null);
  const [powerUserRate, setPowerUserRate] = useState(null);
  const [oracleAddress, setOracleAddress] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const initContract = async () => {
      if (!walletProvider || !isConnected) {
        if (isMounted) setKroSwapContract(null);
        return;
      }

      try {
        const provider = new ethers.BrowserProvider(walletProvider);
        const signer = await provider.getSigner();

        const code = await provider.getCode(KRO_TO_CANDY_SWAP_ADDRESS);
        if (code === '0x') {
          console.error("No contract found at address:", KRO_TO_CANDY_SWAP_ADDRESS);
          if (isMounted) setKroSwapContract(null);
          return;
        }

        const contract = new ethers.Contract(
          KRO_TO_CANDY_SWAP_ADDRESS,
          KroToCandySwapABI,
          signer
        );

        if (isMounted) {
          setKroSwapContract(contract);
          console.log('Contract initialized successfully');
          return contract;
        }
      } catch (err) {
        console.error('Failed to init KroToCandySwap contract:', err);
        if (isMounted) setKroSwapContract(null);
      }
    };

    const fetchAllData = async (contract) => {
      if (!contract || !address) return;

      try {
        // Fetch all data in parallel
        const [oracleAddress, status, base, engaged, powerUser] = await Promise.all([
          contract.statusOracle(),
          new ethers.Contract(
            await contract.statusOracle(),
            oracleAbi,
            contract.runner
          ).getUserStatus(address),
          contract.BASE_RATE(),
          contract.ENGAGED_RATE(),
          contract.POWER_USER_RATE()
        ]);

        if (isMounted) {
          // Prepare rates object
          const rates = {
            base: Number(base) / 100,
            engaged: Number(engaged) / 100,
            powerUser: Number(powerUser) / 100
          };

          // Update all states at once
          setUserStatus(Number(status));
          setOracleAddress(oracleAddress);
          setBaseRate(rates.base);
          setEngagedRate(rates.engaged);
          setPowerUserRate(rates.powerUser);

          // Consolidated log
          console.log('All data fetched and updated:', {
            userStatus: Number(status),
            oracleAddress,
            rates
          });
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        if (isMounted) {
          setUserStatus(null);
          // Note: We're not resetting rates here to keep previous values
        }
      }
    };

    // Main execution flow
    const initialize = async () => {
      console.log('Initializing contract and fetching data...');
      const contract = await initContract();
      if (contract && address && isMounted) {
        await fetchAllData(contract);
      }
    };

    initialize();

    return () => {
      isMounted = false;
    };
  }, [address, isConnected, walletProvider]);

  const connectWallet = useCallback(() => {
    open();
  }, [open]);

  const value = {
    isConnected,
    address,
    chainId,
    open,
    disconnect,
    kroSwapContract,
    userStatus,
    baseRate,
    engagedRate,
    powerUserRate,
    oracleAddress,
    setUserStatus,
  };

  return (
    <Web3Context.Provider value={value}>
      {children}
    </Web3Context.Provider>
  );
};

export default Web3Context;